import React, {
    ReactNode, useCallback, useEffect, useRef, useState
} from 'react'
import { useDispatch } from 'react-redux'
import { useFormikContext } from 'formik'
import styled, { css } from 'styled-components'
import PlaceHolderUser from 'assets/placeholder-user.png'
import { RoleChoices, UpdateMeeting, UserType } from 'generated/graphql'
import { InfoItem } from 'components'
import Button from 'components/Button'
import FullPageLoading from 'components/FullPageLoading'
import Icon from 'components/Icon'
import { removeMeetingParticipant } from 'features/meetings/graphql/mutation'
import { addModal } from 'features/modals/modalSlice'
import {
    colors, formatJobInfo, formatName, _
} from 'utils'
import { useClickOutSide, useDeleteItem, useEventSlug } from 'hooks'
import { useHosts, useParticipants } from 'features/meetings/hooks'
import { Pagination } from 'features/pagination'
import { PlacementPopover } from 'types'
import InviteParticipantForm from './InviteParticipantForm'
import EditRolePopover from './EditRolePopover'

const S = {
    Wrapper: styled.div`
        width: 100%;
    `,
    HeaderWrapper: styled.div`
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        margin-bottom: 3px;
    `,
    Header: styled.span`
        font-size: 12px;
        font-weight: 700;
        color: ${colors.iconOpacity(0.5)};
    `,
    ItemWrapper: styled.div<{ isPending: boolean }>`
        background-color: ${colors.darkOpacity(0.05)};
        padding: 8px 10px;
        margin-bottom: 6px;
        ${({ isPending }) => isPending && css`
            background-color: ${colors.iconOpacity(0.05)};
            border-top: 1px dashed ${colors.iconOpacity(0.25)};
            border-bottom: 1px dashed ${colors.iconOpacity(0.25)};
            & .info-wrapper__name {
                color: ${colors.icon};
                font-weight: 400;
                font-size: 16px;
            }
        `}
    `,
    InfoItem: styled(InfoItem)`
        & .info-actions {
            opacity: 0;
        }
        &:hover .info-actions {
            opacity: 1;
        }
        
    `,
    AddButton: styled(Button)`
        display: block;
        margin: -20px 0 16px auto;
    `,
    ListWrapper: styled.div<{ isExpand: boolean }>`
        display: ${({ isExpand }) => (isExpand ? 'block' : 'none')};
        opacity: ${({ isExpand }) => (isExpand ? 1 : 0)};
        transition: 0.3s ease-in-out;
    `,
    Icon: styled(Icon)<{ isExpand: boolean }>`
        transform: rotate(${({ isExpand }) => (isExpand ? '-180deg' : '0deg')});
        transition: transform 0.25s;
    `,
    Indicator: styled.div`
        font-weight: bold;
        font-size: 14px;
        color: ${colors.icon};
        text-align: right;
        padding-right: 20px;
    `,
    EditRole: styled.div`
        position: absolute;
        top: 100%;
        right: 40px;
        z-index: 1;
    `
}

export interface ParticipantType extends Partial<UserType>{
    userMeetingId: number
    role: RoleChoices
}

interface Props {
    participants?: ParticipantType[],
    hosts?: ParticipantType[],
    meetingId?: number
    spaceId?: number
    fetchData?: () => void
    header?: string
    setTotalParticipants?: (n: number) => void
    renderPagination?: () => ReactNode
    refreshData?: () => void
}

const ParticipantItem = ({
    item, showDeleteConfirmModal, refreshData
}: { item: ParticipantType, showDeleteConfirmModal: (id: number) => void, refreshData: () => void }) => {
    const [showPopover, setShowPopover] = useState(false)
    const popoverRef = useRef<HTMLDivElement>()
    useClickOutSide(popoverRef, () => showPopover && setShowPopover(false))
    const [popoverPlacement, setPopoverPlacement] = useState<PlacementPopover>('bottom')

    useEffect(() => {
        if (showPopover && popoverRef.current) {
            const scrollPage = document.getElementById('scrollableDiv')
            if (popoverRef.current.getBoundingClientRect().bottom > scrollPage.offsetHeight) {
                popoverRef.current.style.top = '0px'
                popoverRef.current.style.transform = 'translateY(-100%)'
                setPopoverPlacement('top')
            } else {
                setPopoverPlacement('bottom')
            }
        }
    }, [showPopover])

    return (
        <div className="position-relative">
            <S.InfoItem
                name={(item.hasLoggedOn
                    ? formatName({ first_name: item.firstName, last_name: item.lastName })
                    : item.email
                )}
                image={item.mugshot || PlaceHolderUser}
                description={formatJobInfo(item)}
                renderExtraInfo={() => (
                    ['Primary speaker', 'Moderator'].includes(item.role)
                        ? (<S.Indicator>{item.role}</S.Indicator>)
                        : <div />
                )}
            >
                <Icon icon="edit" onClick={() => setShowPopover(true)} />
                <Icon icon="trash" onClick={() => showDeleteConfirmModal(item.userMeetingId)} />
            </S.InfoItem>
            {showPopover && (
                <S.EditRole ref={popoverRef}>
                    <EditRolePopover
                        user={item}
                        placement={popoverPlacement}
                        setShowPopover={setShowPopover}
                        refreshData={refreshData}
                    />
                </S.EditRole>
            )}
        </div>
    )
}

const RenderParticipants = ({
    participants, fetchData, header, renderPagination, refreshData
}: Props) => {
    const [isExpand, setIsExpand] = useState(true)
    const handleRemoveParticipant = async (id) => {
        const response = await removeMeetingParticipant(id)
        fetchData()
        return response
    }
    const { showDeleteConfirmModal } = useDeleteItem(
        handleRemoveParticipant, null, {
            title: _('meeting.form.remove_participant_header'),
            description: _('meeting.form.remove_participant_description'),
        }
    )

    const renderItem = useCallback((row: ParticipantType) => (
        <ParticipantItem
            item={row}
            showDeleteConfirmModal={showDeleteConfirmModal}
            refreshData={refreshData}
        />
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [])

    return (
        <>
            <S.HeaderWrapper>
                <S.Header>
                    {header}
                </S.Header>
                <S.Icon
                    icon="chevronDown"
                    size={16}
                    onClick={() => setIsExpand(!isExpand)}
                    isExpand={isExpand}
                />
            </S.HeaderWrapper>
            <S.ListWrapper isExpand={isExpand}>
                {participants.map((participant) => (
                    <S.ItemWrapper isPending={!participant.hasLoggedOn}>
                        {renderItem(participant)}
                    </S.ItemWrapper>
                ))}
                {renderPagination?.()}
            </S.ListWrapper>
        </>
    )
}

const Participants = ({
    meetingId, spaceId, setTotalParticipants
}: Props) => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const {
        list: participants,
        requestStatus: participantRequestStatus,
        pagination: participantPagination,
        totalItems: participantTotalItems,
        handlePageChange: participantHandlePageChange,
        fetchData: fetchParticipants
    } = useParticipants({
        eventId: eventSlug,
        meetingFilters: { id: meetingId },
        spaceFilters: { id: spaceId }
    })
    const {
        list: hosts,
        requestStatus: hostRequestStatus,
        pagination: hostPagination,
        totalItems: hostTotalItems,
        handlePageChange: hostHandlePageChange,
        fetchData: fetchHosts
    } = useHosts({
        eventId: eventSlug,
        meetingFilters: { id: meetingId },
        spaceFilters: { id: spaceId }
    })
    const { values } = useFormikContext<UpdateMeeting>()

    useEffect(() => {
        setTotalParticipants(participantTotalItems + hostTotalItems)
    }, [hostTotalItems, participantTotalItems, setTotalParticipants])

    const showModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <InviteParticipantForm
                    close={close}
                    meetingId={meetingId}
                    fetchParticipants={fetchParticipants}
                    fetchHosts={fetchHosts}
                />
            ),
            header: _('meeting.form.add_participant_header')
        }))
    }

    const refreshDataAfterEditingRole = () => {
        fetchHosts()
        fetchParticipants()
    }

    return (
        <S.Wrapper>
            <S.AddButton
                icon="plusBox"
                onClick={showModal}
                disabled={values.capacity <= participantTotalItems + hostTotalItems}
            >
                {_('meeting.form.add_participant')}
            </S.AddButton>
            {!!hosts.length && (
                <RenderParticipants
                    fetchData={fetchHosts}
                    refreshData={refreshDataAfterEditingRole}
                    participants={hosts.map((e) => (
                        { ...e.user, role: e.role, userMeetingId: e.id }
                    ))}
                    header={_('meeting.form.host_header')}
                    renderPagination={() => (
                        <Pagination
                            page={hostPagination?.page}
                            onPageChange={hostHandlePageChange}
                            totalItems={hostTotalItems}
                        />
                    )}
                />
            )}
            <div style={{ marginBottom: 36 }} />
            {!!participants.length && (
                <>
                    <RenderParticipants
                        fetchData={fetchParticipants}
                        refreshData={refreshDataAfterEditingRole}
                        participants={participants.map((e) => (
                            { ...e.user, role: e.role, userMeetingId: e.id }
                        ))}
                        header={_('meeting.form.participant_header')}
                        renderPagination={() => (
                            <Pagination
                                page={participantPagination?.page}
                                onPageChange={participantHandlePageChange}
                                totalItems={participantTotalItems}
                            />
                        )}
                    />
                </>
            )}
            {
                (participantRequestStatus === 'loading' || hostRequestStatus === 'loading') && (
                    <FullPageLoading isAbsolute />
                )
            }
        </S.Wrapper>
    )
}

export default Participants
